import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { invokeLater } from '../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { AppEvents } from '../AppEvents';
import { AppNavigator } from '../AppNavigator';
import { WorkspaceModel } from '../dashboard/Models';
import { tourCancel } from '../onboarding/OnboardingCommands';
import { asyncOptionsPopupWith, optionsPopupWith } from '../popups/OptionsPopup';
import { PopupManager } from '../popups/PopupManager';
import { ProfileModel } from '../ProfileModel';
import { BrowseWorkspacesPopup } from './BrowseWorkspacesView';
import { CreateWorkspacePopup } from './CreateWorkspacePopup';
import { importBoardProcessPopup } from './ImportBoardProcessPopup';
import { importBoardSelectPopup } from './ImportBoardSelectPopup';
import { WorkspaceInvitePopup } from './WorkspaceInvitePopup';

export const workspaceReload = defineCommand(
    'workspaceReload',
    'Workspace: Reload',
    r => {

        // const api = r.call(ApiClient);
        const model = r.call(WorkspaceModel, false);
        if (!model) return false;

        // const data = await api.exec(getWorkspace(model.id));
        // model.updateTo(WorkspaceModel.create(data).getState());

        return true;
    },
);

export const workspaceCreate = defineCommand(
    'workspaceCreate',
    'Workspace: Create',
    async (r, [label, description, addSelf, makePublic]: [string, string, boolean, boolean?]) => {

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        const workspace = await api.exec(v2.workspaces.create({
            label,
            description,
            addSelf,
            makePublic,
        }));

        evts.push('workspace:create', { workspace });

        if (addSelf) {
            const user = r.call(ProfileModel).getState();
            evts.push('workspace:members:add', { workspace, user });
        }

        return workspace.id;
    },
);

export const workspaceCreateViaPopup = defineCommand(
    'workspaceCreateViaPopup',
    'Workspace: Create...',
    async r => {

        const navigator = r.call(AppNavigator);
        const { actioned, data } = await r.call(PopupManager).invoke({ ui: CreateWorkspacePopup });
        if (actioned) {
            invokeLater(() => {
                navigator.go(`~/${data}`);
            });
        }
        return data || false;
    },
);

const ConfirmDeleteWorkspacePopup = optionsPopupWith(
    'Delete Workspace',
    'Please confirm you want to delete this workspace?  ' +
    'Any boards will be automatically archived.  All folders will be deleted.',
    [
        [true, 'Delete Workspace', 'negative', 'confirm'],
        [false, 'Cancel', 'common', 'cancel'],
    ]
);

export const workspaceDelete = defineCommand(
    'userRemove',
    'Workspace: Delete...',
    async (r, [workspaceId]: [string]) => {

        if (!workspaceId) workspaceId = r.call(WorkspaceModel, false)?.id;
        if (!workspaceId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        const { data } = await r.call(PopupManager).invoke(ConfirmDeleteWorkspacePopup);
        if (!data) return false;

        await api.exec(v2.workspaces.de1ete({ workspaceId }));
        evts.push('workspace:delete', { workspaceId });

        return true;
    }
);

export const workspaceInviteViaPopup = defineCommand(
    'workspaceCreateViaPopup',
    'Workspace: Invite...',
    async (r, [workspaceId]: [string?]) => {

        if (!workspaceId) workspaceId = r.call(WorkspaceModel, false)?.id;
        if (!workspaceId) return false;

        const { actioned, data } = await r.call(PopupManager).invoke({ ui: WorkspaceInvitePopup, args: { workspaceId } });
        return actioned && !!data;
    },
);

export const workspaceRemoveMember = defineCommand(
    'workspaceRemoveMember',
    'Workspace: Remove Member',
    async (r, [userId, workspaceId]: [string, string?]) => {

        if (!workspaceId) workspaceId = r.call(WorkspaceModel, false)?.id;
        if (!workspaceId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const popups = r.call(PopupManager);

        const execute = () => api.exec(v2.workspaces.members.remove({ workspaceId, userIds: [userId] }));
        const popup = asyncOptionsPopupWith(
            'Remove Member',
            'Please confirm you want to remove this member?',
            [
                ['Remove Member', 'negative', execute]
            ]
        );

        const { actioned, data } = await popups.invoke(popup);
        if (!actioned || !data) return false;

        evts.push('workspace:members:remove', { workspaceId, userId });
        return true;
    },
);

export const workspaceJoin = defineCommand(
    'workspaceJoin',
    'Workspace: Join...',
    async (r, [workspaceId]: [string?]) => {

        const api = r.call(ApiClient);
        const dispatch = r.call(CommandDispatcher);
        const evts = r.call(AppEvents);

        dispatch(tourCancel, ['CreateWorkspaceTour']);

        if (!workspaceId) {
            await r.call(PopupManager).invoke({ ui: BrowseWorkspacesPopup });
            return false;
        }

        const workspace = await api.exec(v2.workspaces.join({ workspaceId }));
        const user = r.call(ProfileModel).getState();

        evts.push('workspace:members:add', { workspace, user });
        return true;
    },
);

export const workspaceLeave = defineCommand(
    'workspaceLeave',
    'Workspace: Leave...',
    async (r, [workspaceId]: [string?]) => {

        if (!workspaceId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const profile = r.call(ProfileModel);

        await api.exec(v2.workspaces.leave({ workspaceId }));
        evts.push('workspace:members:remove', { workspaceId, userId: profile.id });

        return true;
    },
);

export const workspaceRename = defineCommand(
    'workspaceRename',
    'Workspace: Rename',
    async (r, [workspaceId, label]: [string, string]) => {

        if (!workspaceId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);
        const workspace = r.call(WorkspaceModel, false);

        await api.exec(v2.workspaces.update({ workspaceId, label }));

        // Patch the current workspace (if in)
        if (workspace?.id === workspaceId) {
            workspace.mutate({ label });
        }

        evts.push('workspace:patch', { workspace: { id: workspaceId, label } });
        return true;
    }
);

export const workspaceSetDescription = defineCommand(
    'workspaceSetDescription',
    'Workspace: Set Description',
    async (r, [workspaceId, value]: [string, string]) => {

        if (!workspaceId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        await api.exec(v2.workspaces.update({ workspaceId, description: value }));
        evts.push('workspace:patch', { workspace: { id: workspaceId, description: value } });

        return true;
    }
);

export const workspaceSetPublic = defineCommand(
    'workspaceSetPublic',
    'Workspace: Set Public',
    async (r, [workspaceId, value]: [string, boolean]) => {

        if (!workspaceId) return false;

        const api = r.call(ApiClient);
        const evts = r.call(AppEvents);

        await api.exec(v2.workspaces.update({ workspaceId, public: value }));
        evts.push('workspace:patch', { workspace: { id: workspaceId, public: value } });

        return true;
    }
);

export const workspaceImportLegacy = defineCommand(
    'workspaceImportLegacy',
    'Workspace: Import Legacy',
    async (r, []: []) => {

        const { actioned, data } = await r.call(PopupManager).invoke(importBoardSelectPopup());
        if (!actioned || !data || !data.list.length) return;

        await r.call(PopupManager).invoke(importBoardProcessPopup(data));
    }
);

